import { useState } from 'react'
import {
  Panel, SectionHeader, ErpTable, Th, Td, EmptyRow, KpiStrip, MetricCard,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { Modal, Field } from '@/shared/ui/Modal'
import {
  createTransportOffer, createTransportRequest, listCellMembers, listCells,
  listTransportOffers, listTransportRequests, matchTransportRequest,
  transportNeedBases, transportOfferTypes, withdrawTransportOffer, pledgeSlots,
  type TransportOffer, type TransportRequest,
} from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { label } from './vocab'

/**
 * Transport — offers, requests, unmatched first.
 *
 * ## The workflow this screen is
 *
 * 1. Somebody with room — a car, or a group walking together — **offers** seats at a
 *    station. `POST /transport/offers`, `turnout:field`.
 * 2. Somebody who cannot get there on their own **requests** a lift, with a need basis
 *    that is recorded and has no OTHER. `POST /transport/requests`, `turnout:field`.
 * 3. A coordinator **matches** the two. The seat claim is a single atomic UPDATE, so two
 *    coordinators matching the same vehicle at the same moment means one gets a 409.
 * 4. The offer flips to FULL when its last seat goes.
 *
 * Steps 1 and 2 had no control anywhere in this application, so the two tables could only
 * ever be empty and the Match button could never have anything to act on. They are here
 * now — without them this screen describes a workflow it cannot start.
 *
 * ## Three things were wrong
 *
 * 1. `GET /turnout/transport/offers` and `/requests` both required a `stationId`
 *    server-side. This page sent only an election, so both calls 400'd. The parameter is
 *    optional now and these are election-wide reads.
 * 2. The loaders' dependency arrays were `[]`, so they ran once with `electionId` still
 *    `null` and never re-ran.
 * 3. "Unmatched" was tested as `statusCode === 'PENDING'`. `RequestStatus` has no
 *    `PENDING` — the open state is `OPEN`, so every request rendered as matched and the
 *    gap this screen exists to surface was invisible even with data present.
 *
 * A transport gap is an OPEN request with no offer against it. That is the number the
 * screen leads with, and matching is the action that closes it.
 */
export function TransportPage() {
  const canRead = useCan('turnout:read')
  const canField = useCan('turnout:field')
  const selection = useElection()
  const { electionId } = selection
  const [matching, setMatching] = useState<TransportRequest | null>(null)
  const [offering, setOffering] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const offers = useLoad(
    () => (electionId ? listTransportOffers(electionId) : Promise.resolve([])),
    [electionId])
  const requests = useLoad(
    () => (electionId ? listTransportRequests(electionId) : Promise.resolve([])),
    [electionId])

  if (!canRead) return <Forbidden what="Transport" />

  const offerRows = offers.data ?? []
  // The server already returns unmatched first; this file does not re-sort.
  const requestRows = requests.data ?? []
  const openRequests = requestRows.filter((r) => r.statusCode === 'OPEN')
  const seatsFree = offerRows
    .filter((o) => o.statusCode === 'AVAILABLE')
    .reduce((sum, o) => sum + Math.max(0, o.seats - o.seatsTaken), 0)

  const withdraw = async (offer: TransportOffer) => {
    setBusy(true); setRowError(null)
    try { await withdrawTransportOffer(offer.id); offers.reload() }
    catch (e) { setRowError(explain(e)) }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Transport"
        description="Peer transport matching. A transport gap is an open request with no offer against it."
        right={(
          <span className="flex items-center gap-2">
            <ElectionPicker selection={selection} />
            <button type="button" className="erp-btn h-9"
              disabled={!electionId} onClick={() => { setRowError(null); setRequesting(true) }}>
              Request a lift
            </button>
            <button type="button" className="erp-btn erp-btn-primary h-9"
              disabled={!electionId} onClick={() => { setRowError(null); setOffering(true) }}>
              Offer transport
            </button>
          </span>
        )}
      />
      <ErrorStrip message={rowError ?? selection.error ?? offers.error ?? requests.error} />

      <KpiStrip>
        <MetricCard label="Transport gaps" value={openRequests.length.toLocaleString()}
          sub="open requests, nobody matched" accent />
        <MetricCard label="Seats free" value={seatsFree.toLocaleString()}
          sub="across available offers" />
        <MetricCard label="Offers" value={offerRows.length.toLocaleString()}
          sub="vehicles and escorts" />
        <MetricCard label="Requests" value={requestRows.length.toLocaleString()}
          sub="all states" />
      </KpiStrip>

      <Panel title="Transport requests" description="Unmatched first — that ordering is the server's.">
        {requests.loading && requestRows.length === 0
          ? <LoadingBlock label="Loading requests…" /> : (
          <ErpTable head={(
            <>
              <Th>Who needs the lift</Th>
              <Th>Why</Th>
              <Th className="text-right">Passengers</Th>
              <Th>Pickup</Th>
              <Th>Status</Th>
              <Th>Station</Th>
              <Th />
            </>
          )}>
            {requestRows.length === 0 ? (
              <EmptyRow colSpan={7}
                message="No transport requests for this election. Use the Request a lift button above to record one." />
            ) : requestRows.map((r) => (
              <tr key={r.id} className="hover:bg-muted/40">
                {/* The person, not their membership uuid. Masked to initials unless the
                    caller holds turnout:read-raw — the same rule the member list follows. */}
                <Td>{r.memberDisplayName ?? (
                  <span className="text-muted-foreground italic">Unnamed member</span>
                )}</Td>
                <Td className="text-muted-foreground">{label(r.needBasisCode)}</Td>
                <Td className="text-right tabular-nums">{r.passengerCount}</Td>
                <Td className="text-muted-foreground">{r.pickupLabel ?? '—'}</Td>
                <Td>
                  {r.statusCode === 'OPEN'
                    ? <span className="text-amber-700 font-medium">Unmatched</span>
                    : <span className="text-muted-foreground">{label(r.statusCode)}</span>}
                </Td>
                {/* Null means the station was dissolved. Saying that beats eight
                     characters of a uuid nobody can act on. */}
                <Td>{r.stationName ?? (
                  <span className="text-muted-foreground italic">Station dissolved</span>
                )}</Td>
                <Td className="text-right">
                  {/*
                     Offered whenever the request is open, and the server's refusal is what
                     speaks — a caller without turnout:field gets a 403 with a sentence,
                     which is more use than a control that silently is not there.
                  */}
                  {r.statusCode === 'OPEN' && (
                    <button type="button" className="erp-btn h-8"
                      disabled={busy} onClick={() => { setRowError(null); setMatching(r) }}>
                      Match
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      <Panel title="Transport offers" description="Available vehicles and escorts, and the ones no longer on offer.">
        {offers.loading && offerRows.length === 0
          ? <LoadingBlock label="Loading offers…" /> : (
          <ErpTable head={(
            <>
              <Th>Type</Th>
              <Th>Offered by</Th>
              <Th className="text-right">Seats free</Th>
              <Th className="text-right">Of</Th>
              <Th>Status</Th>
              <Th>Origin</Th>
              <Th>Station</Th>
              <Th />
            </>
          )}>
            {offerRows.length === 0 ? (
              <EmptyRow colSpan={8}
                message="No transport offers yet. Use the Offer transport button above to record a vehicle or a walking group." />
            ) : offerRows.map((o) => (
              <tr key={o.id} className="hover:bg-muted/40">
                <Td>{label(o.offerTypeCode)}</Td>
                <Td>{o.offeredByDisplayName ?? (
                  <span className="text-muted-foreground">Not recorded</span>
                )}</Td>
                <Td className="text-right tabular-nums">{o.seats - o.seatsTaken}</Td>
                <Td className="text-right tabular-nums text-muted-foreground">{o.seats}</Td>
                <Td className="text-muted-foreground">{label(o.statusCode)}</Td>
                <Td className="text-muted-foreground">{o.originLabel ?? '—'}</Td>
                <Td>{o.stationName ?? (
                  <span className="text-muted-foreground italic">Station dissolved</span>
                )}</Td>
                <Td className="text-right">
                  {o.statusCode === 'AVAILABLE' && canField && (
                    <button type="button" className="erp-btn h-8"
                      disabled={busy} onClick={() => withdraw(o)}>
                      Withdraw
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      {offering && electionId && (
        <OfferModal
          electionId={electionId}
          onClose={() => setOffering(false)}
          onDone={() => { setOffering(false); offers.reload() }}
        />
      )}
      {requesting && electionId && (
        <RequestModal
          electionId={electionId}
          onClose={() => setRequesting(false)}
          onDone={() => { setRequesting(false); requests.reload() }}
        />
      )}
      {matching && (
        <MatchModal
          request={matching}
          offers={offerRows}
          onClose={() => setMatching(null)}
          onDone={() => { setMatching(null); requests.reload(); offers.reload() }}
        />
      )}
    </div>
  )
}

/**
 * A cell and a member picker, shared by both forms.
 *
 * Both an offer and a request are made BY somebody in a cell, and the station comes from
 * the cell rather than being asked twice: a coordinator offering a lift is offering it at
 * the station their cell votes at, and letting the two disagree is how a request gets
 * matched to a vehicle going somewhere else.
 */
function useCellMemberPicker(electionId: string) {
  const [cellId, setCellId] = useState('')
  const [membershipId, setMembershipId] = useState('')
  const cells = useLoad(
    () => listCells({ electionId, status: 'ACTIVE' }), [electionId])
  const members = useLoad(
    () => (cellId ? listCellMembers(cellId) : Promise.resolve([])), [cellId])
  const cellRows = cells.data ?? []
  const cell = cellRows.find((c) => c.id === cellId)
  return {
    cellId, setCellId, membershipId, setMembershipId,
    cells, members, cellRows, cell,
    reset: () => { setCellId(''); setMembershipId('') },
  }
}

/** Record a vehicle or a walking group with room in it. */
function OfferModal({ electionId, onClose, onDone }: {
  electionId: string
  onClose: () => void
  onDone: () => void
}) {
  const picker = useCellMemberPicker(electionId)
  const types = useLoad(transportOfferTypes, [])
  const slots = useLoad(pledgeSlots, [])
  const [offerTypeCode, setOfferTypeCode] = useState('')
  const [seats, setSeats] = useState('4')
  const [originLabel, setOriginLabel] = useState('')
  const [windowSlotCode, setWindowSlotCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /*
     The cell carries BOTH the station id and the ltree path now, so there is no second
     lookup. There used to be one against `/geography/units`, which is gated on
     `geography:read` — an authority a turnout operator need not hold. It 403'd, `station`
     stayed undefined, `ready` was false, and the Offer button was permanently disabled:
     the form filled in fine and the button did nothing, with no message anywhere.
  */
  const submit = async () => {
    if (!picker.cell || !offerTypeCode) {
      setError('Choose a cell and a transport type first.')
      return
    }
    setBusy(true); setError(null)
    try {
      await createTransportOffer({
        electionId,
        cellId: picker.cellId,
        stationId: picker.cell.stationId,
        unitPath: picker.cell.unitPath,
        offeredByMemberId: picker.membershipId || null,
        offerTypeCode,
        seats: Number(seats),
        originLabel: originLabel || null,
        windowSlotCode: windowSlotCode || null,
      })
      onDone()
    } catch (e) { setError(explain(e)); setBusy(false) }
  }

  const ready = picker.cell && offerTypeCode && Number(seats) >= 1

  return (
    <Modal open onClose={onClose} width="lg" title="Offer transport"
      description="A vehicle with spare seats, or a group walking to the station together."
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !ready} onClick={submit}>
            {busy ? 'Saving…' : 'Offer'}
          </button>
        </>
      )}>
      <ErrorStrip message={error ?? picker.cells.error} />
      <div className="space-y-3">
        <Field label="Cell" required
          hint={!picker.cells.loading && picker.cellRows.length === 0
            ? 'No active cells in this election, so there is nowhere to offer transport to yet.'
            : 'The station is taken from the cell — an offer and the request it fills must be going to the same place.'}>
          <select className="erp-input h-9 w-full" value={picker.cellId}
            disabled={picker.cells.loading}
            onChange={(e) => { picker.setCellId(e.target.value); picker.setMembershipId('') }}>
            <option value="">Choose a cell…</option>
            {picker.cellRows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label ?? c.code}{c.stationName ? ` — ${c.stationName}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Offered by"
          hint="Which member is providing it. Optional — a cell can offer collectively.">
          <select className="erp-input h-9 w-full" value={picker.membershipId}
            disabled={!picker.cellId || picker.members.loading}
            onChange={(e) => picker.setMembershipId(e.target.value)}>
            <option value="">The cell, collectively</option>
            {(picker.members.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.displayName}</option>
            ))}
          </select>
        </Field>
        <Field label="Type" required>
          <select className="erp-input h-9 w-full" value={offerTypeCode}
            disabled={types.loading}
            onChange={(e) => setOfferTypeCode(e.target.value)}>
            <option value="">Choose…</option>
            {(types.data ?? []).map((t) => (
              <option key={t} value={t}>{label(t)}</option>
            ))}
          </select>
        </Field>
        <Field label="Seats" required hint="How many people it can take, including nobody yet.">
          <input className="erp-input h-9 w-full" type="number" min="1" value={seats}
            onChange={(e) => setSeats(e.target.value)} />
        </Field>
        <Field label="Setting off from"
          hint="Where riders meet it. A landmark beats an address here.">
          <input className="erp-input h-9 w-full" value={originLabel}
            onChange={(e) => setOriginLabel(e.target.value)} />
        </Field>
        <Field label="When">
          <select className="erp-input h-9 w-full" value={windowSlotCode}
            disabled={slots.loading}
            onChange={(e) => setWindowSlotCode(e.target.value)}>
            <option value="">Any time</option>
            {(slots.data ?? []).map((sl) => (
              <option key={sl} value={sl}>{label(sl)}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  )
}

/** Record that somebody cannot get to their station on their own. */
function RequestModal({ electionId, onClose, onDone }: {
  electionId: string
  onClose: () => void
  onDone: () => void
}) {
  const picker = useCellMemberPicker(electionId)
  const bases = useLoad(transportNeedBases, [])
  const slots = useLoad(pledgeSlots, [])
  const [needBasisCode, setNeedBasisCode] = useState('')
  const [passengerCount, setPassengerCount] = useState('1')
  const [pickupLabel, setPickupLabel] = useState('')
  const [windowSlotCode, setWindowSlotCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!picker.cell || !picker.membershipId || !needBasisCode) {
      setError('Choose a cell, the member who needs the lift, and why.')
      return
    }
    setBusy(true); setError(null)
    try {
      await createTransportRequest({
        electionId,
        cellId: picker.cellId,
        stationId: picker.cell.stationId,
        unitPath: picker.cell.unitPath,
        memberId: picker.membershipId,
        pickupLabel: pickupLabel || null,
        passengerCount: Number(passengerCount),
        needBasisCode,
        windowSlotCode: windowSlotCode || null,
      })
      onDone()
    } catch (e) { setError(explain(e)); setBusy(false) }
  }

  const ready = picker.cell && picker.membershipId && needBasisCode

  return (
    <Modal open onClose={onClose} width="lg" title="Request a lift"
      description="Somebody who cannot get to their station on their own. This becomes a transport gap until an offer is matched to it."
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !ready} onClick={submit}>
            {busy ? 'Saving…' : 'Request'}
          </button>
        </>
      )}>
      <ErrorStrip message={error ?? picker.cells.error} />
      <div className="space-y-3">
        <Field label="Cell" required
          hint={!picker.cells.loading && picker.cellRows.length === 0
            ? 'No active cells in this election yet.'
            : undefined}>
          <select className="erp-input h-9 w-full" value={picker.cellId}
            disabled={picker.cells.loading}
            onChange={(e) => { picker.setCellId(e.target.value); picker.setMembershipId('') }}>
            <option value="">Choose a cell…</option>
            {picker.cellRows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label ?? c.code}{c.stationName ? ` — ${c.stationName}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Who needs it" required
          hint={picker.cellId && !picker.members.loading
            && (picker.members.data ?? []).length === 0
            ? 'This cell has no active members, so nobody can be recorded as needing a lift.'
            : undefined}>
          <select className="erp-input h-9 w-full" value={picker.membershipId}
            disabled={!picker.cellId || picker.members.loading}
            onChange={(e) => picker.setMembershipId(e.target.value)}>
            <option value="">Choose a member…</option>
            {(picker.members.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.displayName}</option>
            ))}
          </select>
        </Field>
        <Field label="Why" required
          hint="Recorded, and there is deliberately no OTHER — a reason nobody can read is a reason nobody can audit.">
          <select className="erp-input h-9 w-full" value={needBasisCode}
            disabled={bases.loading}
            onChange={(e) => setNeedBasisCode(e.target.value)}>
            <option value="">Choose…</option>
            {(bases.data ?? []).map((b) => (
              <option key={b} value={b}>{label(b)}</option>
            ))}
          </select>
        </Field>
        <Field label="Passengers" required
          hint="Counts against an offer's seats as one claim — a party of three does not fit in two seats.">
          <input className="erp-input h-9 w-full" type="number" min="1" value={passengerCount}
            onChange={(e) => setPassengerCount(e.target.value)} />
        </Field>
        <Field label="Pick up from">
          <input className="erp-input h-9 w-full" value={pickupLabel}
            onChange={(e) => setPickupLabel(e.target.value)} />
        </Field>
        <Field label="When">
          <select className="erp-input h-9 w-full" value={windowSlotCode}
            disabled={slots.loading}
            onChange={(e) => setWindowSlotCode(e.target.value)}>
            <option value="">Any time</option>
            {(slots.data ?? []).map((sl) => (
              <option key={sl} value={sl}>{label(sl)}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  )
}

/**
 * Pick the offer that fills this request.
 *
 * Only offers at the same station with room for the whole party are listed: matching a
 * four-passenger request to a vehicle with two seats left is a 409 from the seat-claim
 * update, and the coordinator finds out after tapping rather than before.
 */
function MatchModal({ request, offers, onClose, onDone }: {
  request: TransportRequest
  offers: TransportOffer[]
  onClose: () => void
  onDone: () => void
}) {
  const [offerId, setOfferId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const candidates = offers.filter((o) =>
    o.statusCode === 'AVAILABLE'
    && o.stationId === request.stationId
    && o.seats - o.seatsTaken >= request.passengerCount)

  const submit = async () => {
    if (!offerId) return
    setBusy(true); setError(null)
    try { await matchTransportRequest(request.id, offerId); onDone() }
    // The seat claim is atomic: two coordinators matching the same vehicle at the same
    // moment means one of them gets a 409, and the sentence explains it.
    catch (e) { setError(explain(e)); setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title="Match this request"
      description={`${request.passengerCount} passenger${request.passengerCount === 1 ? '' : 's'} · ${label(request.needBasisCode)}`}
      footer={(
        <>
          <button type="button" className="erp-btn h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={busy || !offerId} onClick={submit}>
            {busy ? 'Matching…' : 'Match'}
          </button>
        </>
      )}>
      <ErrorStrip message={error} />
      <Field label="Offer" required
        hint="Only offers at this station with room for the whole party are listed.">
        <select className="erp-input h-9 w-full" value={offerId}
          onChange={(e) => setOfferId(e.target.value)}>
          <option value="">Choose an offer…</option>
          {candidates.map((o) => (
            <option key={o.id} value={o.id}>
              {label(o.offerTypeCode)} — {o.seats - o.seatsTaken} seat
              {o.seats - o.seatsTaken === 1 ? '' : 's'} free
              {o.offeredByDisplayName ? ` · ${o.offeredByDisplayName}` : ''}
              {o.originLabel ? ` · ${o.originLabel}` : ''}
            </option>
          ))}
        </select>
      </Field>
      {candidates.length === 0 && (
        <p className="text-erp-xs text-muted-foreground">
          Nothing at this station has room for {request.passengerCount} passenger
          {request.passengerCount === 1 ? '' : 's'}. This request is the transport gap.
        </p>
      )}
    </Modal>
  )
}
